import { useEffect, useState } from 'react';
import { apiRequest } from '@/lib/api/client';
import type { TeacherStudent } from '@/features/teacher/types';
type Meeting = {
  id: string;
  title: string;
  scheduled_at: string;
  location_or_url?: string;
  invite_status?: string;
  discipline_code?: string;
};
export function CourseMeetings({
  teacher = false,
  code,
  group,
  students = [],
}: {
  teacher?: boolean;
  code: string;
  group?: string;
  students?: TeacherStudent[];
}) {
  const [meetings, setMeetings] = useState<Meeting[]>([]);
  const [title, setTitle] = useState('');
  const [when, setWhen] = useState('');
  const [place, setPlace] = useState('');
  const [members, setMembers] = useState<string[]>([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const path = `/${teacher ? 'teacher' : 'course-student'}/meetings`;
  useEffect(() => {
    let active = true;
    setMeetings([]);
    setMembers([]);
    setError('');
    void apiRequest<Meeting[]>(path, { channel: 'platform' })
      .then((rows) => {
        if (active) setMeetings(rows);
      })
      .catch((e) => {
        if (active) setError(e.message);
      });
    return () => {
      active = false;
    };
  }, [path, code, group]);
  const run = async (action: () => Promise<unknown>) => {
    setBusy(true);
    setError('');
    try {
      await action();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Не удалось сохранить');
    } finally {
      setBusy(false);
    }
  };
  return (
    <section className="space-y-3 text-sm">
      <h2>Встречи и консультации</h2>
      {error && <p role="alert">{error}</p>}
      {teacher && code && (
        <details>
          <summary>Создать консультацию</summary>
          <input
            className="bg-slate-900"
            aria-label="Тема встречи"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
          />
          <input
            className="bg-slate-900"
            type="datetime-local"
            aria-label="Дата и время встречи"
            value={when}
            onChange={(e) => setWhen(e.target.value)}
          />
          <input
            className="bg-slate-900"
            aria-label="Место или ссылка"
            value={place}
            onChange={(e) => setPlace(e.target.value)}
          />
          {students
            .filter((s) => s.user_id)
            .map((s) => (
              <label className="block" key={s.id}>
                <input
                  type="checkbox"
                  checked={members.includes(s.user_id!)}
                  onChange={(e) =>
                    setMembers((prev) =>
                      e.target.checked
                        ? [...prev, s.user_id!]
                        : prev.filter((id) => id !== s.user_id),
                    )
                  }
                />
                {s.full_name} · {s.student_code}
              </label>
            ))}
          <button
            disabled={busy || !title.trim() || !when || !members.length}
            onClick={() =>
              void run(async () => {
                const result = await apiRequest<Meeting>(path, {
                  method: 'POST',
                  channel: 'platform',
                  body: {
                    title,
                    scheduled_at: when,
                    location_or_url: place,
                    participants: members,
                    discipline_code: code,
                    group_code: group,
                  },
                });
                setMeetings((prev) => [...prev, result]);
                setTitle('');
                setMembers([]);
              })
            }
          >
            Создать приглашение
          </button>
        </details>
      )}
      {meetings
        .filter((m) => !m.discipline_code || m.discipline_code === code)
        .map((m) => (
          <article key={m.id} className="border border-slate-700 p-4">
            <h3>{m.title}</h3>
            <p>
              {m.scheduled_at} · {m.location_or_url}
            </p>
            <p>{m.invite_status || 'Приглашение создано'}</p>
            {!teacher &&
              ['ACCEPTED', 'DECLINED'].map((response) => (
                <button
                  key={response}
                  disabled={busy}
                  onClick={() =>
                    void run(async () => {
                      await apiRequest(`${path}/${m.id}/response?response=${response}`, {
                        method: 'POST',
                        channel: 'platform',
                      });
                      setMeetings((prev) =>
                        prev.map((row) =>
                          row.id === m.id ? { ...row, invite_status: response } : row,
                        ),
                      );
                    })
                  }
                >
                  {response === 'ACCEPTED' ? 'Принять' : 'Отклонить'}
                </button>
              ))}
          </article>
        ))}
      {!meetings.length && <p>Встречи еще не назначены.</p>}
    </section>
  );
}
